import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { SignatureIcon } from '@phosphor-icons/react';
import {
  Checkbox,
  ChoiceGroup,
  Select,
  Tooltip,
  cn,
} from '@skupreme/shared/ui';
import type { OrderRequestedShipping } from '@skupreme/api-contracts/core';
import { CatalogHelpText } from '@utility/constants/strings';
import { fulfillmentTypeAppearance } from '../../lib/fulfillment-type';
import { requestedShippingText } from '../../lib/requested-shipping';
import type { ShippingFormValues } from '../../lib/shipping-form';
import { LabeledField, type LegacyOption } from './fields';

/** Past this many speeds the cards wrap into a wall, and a list reads better. */
const MAX_CARDS = 4;

function TypeIcon({ value }: { value: string }) {
  const { icon: Icon } = fulfillmentTypeAppearance(value);

  return <Icon size={14} weight="duotone" />;
}

/** The icon in its tint, for the select's rows where there is no card to tint. */
function TypeDisc({ value }: { value: string }) {
  return (
    <span
      className={cn(
        'sk:flex sk:size-8 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full',
        fulfillmentTypeAppearance(value).className,
      )}
    >
      <TypeIcon value={value} />
    </span>
  );
}

export type FulfillmentTypeFieldProps = {
  control: Control<ShippingFormValues>;
  errors: FieldErrors<ShippingFormValues>;
  options: LegacyOption[];
  /** What the marketplace asked for, which is what the speed defaults to. */
  requested?: OrderRequestedShipping | null;
};

/** How fast the warehouse ships it — every speed on offer, one click each. */
export function FulfillmentTypeField({
  control,
  errors,
  options,
  requested,
}: FulfillmentTypeFieldProps) {
  const speeds = (options || []).filter(option => !!option?.value);

  return (
    <Controller
      control={control}
      name="shipping_speed"
      render={({ field }) => {
        const value = typeof field.value === 'string' ? field.value : '';

        return (
          <LabeledField
            label="Fulfillment type"
            error={errors.shipping_speed?.message}
          >
            {speeds.length > MAX_CARDS ? (
              <Select
                aria-label="Fulfillment type"
                options={speeds.map(speed => ({
                  value: speed.value,
                  label: speed.text,
                  leading: <TypeDisc value={speed.value} />,
                }))}
                value={value}
                placeholder="Select fulfillment type"
                onChange={field.onChange}
              />
            ) : (
              <ChoiceGroup
                label="Fulfillment type"
                options={speeds.map(speed => ({
                  value: speed.value,
                  label: speed.text,
                  icon: <TypeIcon value={speed.value} />,
                  iconClassName: fulfillmentTypeAppearance(speed.value)
                    .className,
                }))}
                value={value}
                onChange={field.onChange}
              />
            )}
            <span className="sk:text-xs sk:text-fg-tertiary">
              {requestedShippingText(requested)}
            </span>
          </LabeledField>
        );
      }}
    />
  );
}

export type SignatureOptionProps = {
  isSelected: boolean;
  onChange: (value: boolean) => void;
};

/**
 * An add-on to the service above, not a field of its own: it prices with the
 * quote, so it wears the same card as the speeds and sits right under them.
 */
export function SignatureOption({
  isSelected,
  onChange,
}: SignatureOptionProps) {
  return (
    <Checkbox
      isSelected={isSelected}
      onChange={onChange}
      className={cn(
        'sk:h-10 sk:w-full sk:items-center sk:justify-start sk:rounded-option sk:px-3',
        'sk:transition-[box-shadow,background-color] sk:duration-100',
        isSelected
          ? 'sk:inset-shadow-selected sk:bg-fill-quaternary'
          : 'sk:inset-shadow-hairline sk:bg-surface sk:hover:bg-fill-quaternary',
      )}
    >
      <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
        <span
          className={cn(
            'sk:flex sk:size-6 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full',
            isSelected
              ? 'sk:bg-success-bg sk:text-success'
              : 'sk:bg-fill-secondary sk:text-fg-tertiary',
          )}
        >
          <SignatureIcon size={14} weight="duotone" />
        </span>
        <span className="sk:flex sk:min-w-0 sk:items-center sk:whitespace-nowrap">
          Signature required
          <Tooltip content={CatalogHelpText.shipWithSignature}>
            <span className="sk:ml-1 sk:text-xs sk:text-fg-tertiary sk:underline sk:decoration-dotted">
              on delivery
            </span>
          </Tooltip>
        </span>
      </span>
    </Checkbox>
  );
}
